import { useEffect, useState, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import type { TeamPublic, SportPublic, AgeGroupPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function TeamEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [team, setTeam] = useState<TeamPublic | null>(null);
  const [ageGroupId, setAgeGroupId] = useState("");
  const [sportId, setSportId] = useState("");
  // 157 -- house doctor / medical practice
  const [doctorName, setDoctorName] = useState("");
  const [doctorPractice, setDoctorPractice] = useState("");
  const [doctorPhone, setDoctorPhone] = useState("");
  const [sports, setSports] = useState<SportPublic[]>([]);
  const [ageGroups, setAgeGroups] = useState<AgeGroupPublic[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api.getTeam(id)
      .then((teamRes) => {
        setTeam(teamRes.team);
        setAgeGroupId(teamRes.team.age_group_id ?? "");
        setSportId(teamRes.team.sport_id ?? "");
        setDoctorName(teamRes.team.house_doctor_name ?? "");
        setDoctorPractice(teamRes.team.house_doctor_practice ?? "");
        setDoctorPhone(teamRes.team.house_doctor_phone ?? "");
        // Load school-scoped sports
        return api.getSchoolSports(teamRes.team.school_id).then((sportsRes) => {
          setSports(sportsRes.sports.map((s) => ({ id: s.sport_id, name: s.sport_name, default_court_type_id: null, court_type_name: s.court_type_name ?? null, enabled: true })));
        });
      })
      .catch((e) =>
        setError(e instanceof api.ApiError ? e.message : "Failed to load team"),
      )
      .finally(() => setLoading(false));
  }, [id]);

  // Fetch age groups when sport changes
  useEffect(() => {
    if (!team?.school_id || !sportId) {
      setAgeGroups([]);
      return;
    }
    api.getAgeGroups(team.school_id, sportId)
      .then((res) => setAgeGroups(res.age_groups))
      .catch(() => setAgeGroups([]));
  }, [team?.school_id, sportId]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setError(null);
    setSubmitting(true);

    try {
      await api.updateTeam(id, {
        age_group_id: ageGroupId || null,
        sport_id: sportId || null,
        house_doctor_name: doctorName.trim() || null,
        house_doctor_practice: doctorPractice.trim() || null,
        house_doctor_phone: doctorPhone.trim() || null,
      });
      navigate(`/teams/${id}`);
    } catch (err) {
      setError(err instanceof api.ApiError ? err.message : "Failed to update team");
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) return <p className="text-muted-foreground" data-page="team-edit" data-state="loading">Loading...</p>;
  if (!team) return <p className="text-muted-foreground" data-page="team-edit" data-state="error">Team not found</p>;

  return (
    <div className="flex flex-col gap-6" data-page="team-edit" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Edit Team</h1>
        <p className="text-muted-foreground">Update team details.</p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Team Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-region="form" data-form="team-edit">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label>Name</Label>
              <p className="text-sm text-muted-foreground" data-field="display-name">
                {team.display_name}
                <span className="ml-2 text-xs opacity-70">(auto-generated from age group + rank)</span>
              </p>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="sport">Sport</Label>
              <select
                id="sport"
                data-field="sport"
                value={sportId}
                onChange={(e) => setSportId(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                required
              >
                <option value="">Select a sport</option>
                {sports.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex flex-col gap-2">
              <Label htmlFor="age-group">Age Group</Label>
              <select
                id="age-group"
                data-field="age-group"
                value={ageGroupId}
                onChange={(e) => setAgeGroupId(e.target.value)}
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                disabled={!sportId}
                required
              >
                {!sportId && <option value="">Select a sport first</option>}
                {ageGroups.map((ag) => (
                  <option key={ag.id} value={ag.id}>
                    {ag.name}
                  </option>
                ))}
              </select>
            </div>

            {/* 157 -- house doctor / medical practice (BokSmart medical section) */}
            <div className="flex flex-col gap-2" data-field="house-doctor">
              <Label htmlFor="doctor-name">House doctor</Label>
              <input
                id="doctor-name"
                value={doctorName}
                onChange={(e) => setDoctorName(e.target.value)}
                placeholder="Doctor name"
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <input
                value={doctorPractice}
                onChange={(e) => setDoctorPractice(e.target.value)}
                placeholder="Medical practice"
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
              <input
                value={doctorPhone}
                onChange={(e) => setDoctorPhone(e.target.value)}
                placeholder="Practice phone"
                className="flex h-10 rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              />
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
              <Link to={`/teams/${id}`} className={buttonVariants({ variant: "outline" })} data-action="cancel">
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
